import React, { useEffect, useState, useRef } from "react";
import {
  FileText,
  CheckCircle,
  ArrowRight,
  ShieldCheck,
  Zap,
  Table2,
  Eye,
  Download,
  BarChart3,
  Clock,
  Target,
  Users,
  Star,
  ChevronRight,
  Layers,
  Brain,
  Scan,
  Ship,
  Briefcase,
  Building2,
  Truck,
  X,
  Menu,
} from "lucide-react";

// ───────────────────────────────────────────────
// DocuAI Landing Page — 물류 특화 비정형 문서 추출 솔루션
// ───────────────────────────────────────────────

const LandingPage = ({ onLoginClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [visibleSections, setVisibleSections] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activePricing, setActivePricing] = useState("personal");
  const [selectedSeats, setSelectedSeats] = useState(3);

  // loginHS 톤 — near-black 버튼 + 라벤더-그레이 보조 (자극적 노랑·강인디고 제거)
  const DARK = "#1b1a20"; // primary 버튼 (near-black)
  const SOFT_TX = "#6a6593"; // eyebrow·뱃지 텍스트 (라벤더-그레이)

  // 비즈니스 인원별 요금 데이터 (priceData.ts 기준)
  const businessPricing = {
    3: { pages: "7,500", base: 110000, discount: 0, total: 110000 },
    4: { pages: "8,500", base: 123200, discount: 2200, total: 121000 },
    5: { pages: "9,500", base: 136400, discount: 4400, total: 132000 },
    7: { pages: "11,500", base: 162800, discount: 8800, total: 154000 },
    10: { pages: "14,500", base: 202400, discount: 15400, total: 187000 },
    15: { pages: "19,500", base: 268400, discount: 26400, total: 242000 },
    20: { pages: "24,500", base: 334400, discount: 37400, total: 297000 },
  };
  const sectionRefs = useRef({});

  useEffect(() => {
    setIsVisible(true);

    // Intersection Observer for scroll animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => ({
              ...prev,
              [entry.target.id]: true,
            }));
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -50px 0px" },
    );

    // Observe all sections
    setTimeout(() => {
      document.querySelectorAll("[data-animate]").forEach((el) => {
        observer.observe(el);
      });
    }, 100);

    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <div
      className="min-h-screen bg-white text-gray-900 overflow-x-hidden"
      style={{ fontFamily: "'Inter', 'Pretendard', -apple-system, sans-serif" }}
    >
      {/* Google Fonts */}
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap"
        rel="stylesheet"
      />

      {/* ─── Navigation ─── */}
      <nav className="fixed w-full bg-white/80 backdrop-blur-xl z-50 border-b border-gray-100/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="flex justify-between items-center"
            style={{ height: "72px" }}
          >
            <div className="flex items-center gap-10">
              <div className="flex items-center gap-2.5">
                <img
                  src="/docuai_logo.png"
                  alt="DocuAI"
                  className="w-9 h-9 rounded-lg"
                />
                <span
                  className="text-2xl font-extrabold tracking-tight"
                  style={{ color: "#1a1a1a" }}
                >
                  DocuAI
                </span>
              </div>
              <div className="hidden md:flex items-center gap-8">
                <button
                  onClick={() => scrollTo("features")}
                  className="text-base text-gray-700 hover:text-gray-900 transition font-semibold"
                >
                  핵심 기능
                </button>
                <button
                  onClick={() => scrollTo("workflow")}
                  className="text-base text-gray-700 hover:text-gray-900 transition font-semibold"
                >
                  작동 방식
                </button>
                <button
                  onClick={() => scrollTo("customers")}
                  className="text-base text-gray-700 hover:text-gray-900 transition font-semibold"
                >
                  고객
                </button>
                <button
                  onClick={() => scrollTo("pricing")}
                  className="text-base text-gray-700 hover:text-gray-900 transition font-semibold"
                >
                  요금제
                </button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => scrollTo("cta-section")}
                className="hidden sm:inline-flex text-sm text-gray-600 hover:text-gray-900 transition font-medium"
              >
                도입문의
              </button>
              <button
                onClick={onLoginClick}
                className="hidden sm:inline-flex px-4 py-2 rounded-lg text-gray-700 font-semibold text-sm transition-all hover:bg-gray-100 border border-gray-300"
              >
                로그인
              </button>
              <button
                onClick={onLoginClick}
                className="hidden sm:inline-flex px-5 py-2.5 rounded-lg text-white font-semibold text-sm transition-all hover:opacity-90"
                style={{
                  background: DARK,
                  boxShadow: "0 6px 16px -6px rgba(20,21,26,0.30)",
                }}
              >
                무료 체험 시작
              </button>
              <button
                onClick={onLoginClick}
                className="sm:hidden px-4 py-2 rounded-lg text-white font-semibold text-sm"
                style={{ backgroundColor: DARK }}
              >
                시작
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* ─── Hero Section ─── */}
      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-32 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 -z-10">
          <div
            className="absolute inset-0"
            style={{
              background: "linear-gradient(135deg, #fafafa, #f5f5f5, #fafafa)",
            }}
          />
          <div
            className="absolute top-20 right-10 w-[600px] h-[600px] rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(0,0,0,0.03)" }}
          />
          <div
            className="absolute -bottom-20 -left-20 w-[500px] h-[500px] rounded-full blur-3xl"
            style={{ backgroundColor: "rgba(0,0,0,0.02)" }}
          />
          {/* Grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "radial-gradient(circle, #1e293b 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`max-w-4xl mx-auto text-center transition-all duration-1000 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-8"
              style={{
                backgroundColor: "#f1effa",
                borderColor: "#e6e6f2",
              }}
            >
              <span className="relative flex h-2 w-2">
                <span
                  className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                  style={{ backgroundColor: SOFT_TX }}
                ></span>
                <span
                  className="relative inline-flex rounded-full h-2 w-2"
                  style={{ backgroundColor: SOFT_TX }}
                ></span>
              </span>
              <span
                className="text-sm font-semibold"
                style={{ color: SOFT_TX }}
              >
                VLM 기반 물류 문서 AI 솔루션
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mb-6">
              <span className="text-gray-900">수기 입력은</span>
              <br />
              <span className="text-gray-900">과거의 일이 됩니다</span>
            </h1>

            {/* Sub-headline */}
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10 leading-relaxed">
              인보이스부터 B/L까지, 복잡한{" "}
              <strong className="text-gray-800">표 구조를 완벽하게 인식</strong>
              하는
              <br className="hidden sm:block" />
              VLM 기반 AI로 수출입 서류를 엑셀 데이터로 바꾸는 가장 빠른 방법.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6">
              <button
                onClick={onLoginClick}
                className="px-8 py-4 rounded-xl font-bold text-lg transition-all hover:-translate-y-0.5 flex items-center justify-center gap-2"
                style={{
                  background: DARK,
                  color: "#fff",
                  boxShadow: "0 10px 24px -8px rgba(20,21,26,0.35)",
                }}
              >
                7일간 무료 체험하기
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollTo("features")}
                className="px-8 py-4 rounded-xl bg-white text-gray-700 font-bold text-lg border border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm"
              >
                서비스 소개 보기
              </button>
            </div>

            <p className="text-xs text-gray-400">
              신용카드 없이 · 설치 없이 · 7일 무료
            </p>
          </div>

          {/* Stats */}
          <div
            className={`mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto transition-all duration-1000 delay-300 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
          >
            {[
              { value: "99%", label: "데이터 정확도", icon: Target },
              { value: "100x", label: "처리 속도 향상", icon: Zap },
              { value: "80%", label: "업무 시간 단축", icon: Clock },
              { value: "2주", label: "평균 도입 기간", icon: BarChart3 },
            ].map((stat, i) => (
              <div
                key={i}
                className="bg-white/70 backdrop-blur rounded-xl p-4 border border-gray-100 text-center shadow-sm"
              >
                <stat.icon
                  className="w-5 h-5 mx-auto mb-2"
                  style={{ color: SOFT_TX }}
                />
                <div className="text-2xl font-extrabold text-gray-900">
                  {stat.value}
                </div>
                <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Hero Image / Demo Preview */}
          <div
            className={`mt-16 relative mx-auto max-w-5xl transition-all duration-1000 delay-500 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-16 opacity-0"}`}
          >
            <div className="relative rounded-2xl bg-gradient-to-b from-gray-900 to-gray-800 p-2 shadow-2xl ring-1 ring-white/10">
              <div className="rounded-xl overflow-hidden aspect-[16/9] relative bg-gray-800">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/Mcuq-_pCsfs?autoplay=1&mute=1&loop=1&playlist=Mcuq-_pCsfs&controls=0&modestbranding=1&rel=0&playsinline=1&showinfo=0&iv_load_policy=3"
                  title="DocuAI 소개 영상"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
                {/* Light overlay for text contrast (pointer-events-none 으로 iframe 클릭 가능) */}
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/10 to-transparent pointer-events-none" />
              </div>
              {/* Browser chrome dots */}
              <div className="absolute top-4 left-5 flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/80"></div>
                <div className="w-2.5 h-2.5 rounded-full bg-green-400/80"></div>
              </div>
            </div>
            {/* Glow effect */}
            <div className="absolute -inset-x-20 -bottom-20 h-40 bg-gradient-to-t from-white via-white to-transparent" />
          </div>
        </div>
      </section>

      {/* ─── Trust Bar (Logos) ─── */}
      <section className="py-12 bg-gray-50/50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-center text-sm text-gray-400 mb-8 font-medium">
            수출입 물류 기업들이 신뢰하는 솔루션
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-40">
            {[
              "관세법인 A",
              "포워딩 B사",
              "무역상사 C",
              "운송사 D",
              "물류기업 E",
            ].map((name, i) => (
              <div
                key={i}
                className="text-lg font-bold text-gray-400 tracking-wide"
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Core Features ─── */}
      <section id="features" className="py-24 bg-white" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 transition-all duration-700 ${visibleSections["features"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            <p
              className="text-sm font-bold tracking-widest uppercase mb-3"
              style={{ color: SOFT_TX }}
            >
              Core Technology
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              당신의 표는 <span className="text-gray-900">깨지지 않습니다</span>
            </h2>
            <p className="text-lg text-gray-500 max-w-2xl mx-auto">
              단순 OCR이 아닌, 문서의 구조를 이해하는 VLM 기술로
              <br className="hidden sm:block" />
              수출입 현장의 비정형 문서를 완벽하게 데이터화합니다.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Table2,
                color: "blue",
                title: "지능형 표 구조 인식",
                subtitle: "Table Structure Recognition",
                description:
                  "행과 열의 관계를 정교하게 인식하여 표 형식을 그대로 유지합니다. 엑셀 출력 시 별도 가공 없이 즉시 활용 가능합니다.",
                highlight: "사후 편집 90% 감소",
              },
              {
                icon: Brain,
                color: "indigo",
                title: "물류 도메인 특화 엔진",
                subtitle: "Domain-Specific AI Engine",
                description:
                  "B/L, 인보이스, 패킹 리스트, FTA 서류까지. 물류 현장의 언어를 이해하는 AI가 데이터를 정밀하게 추출합니다.",
                highlight: "99% 데이터 정확도",
              },
              {
                icon: Eye,
                color: "purple",
                title: "실무 최적화 도구",
                subtitle: "Practical Tool Suite",
                description:
                  "줄자 도구로 검증하고, 스위칭 버튼으로 숫자 표기법(유럽/미국식)을 전환하세요. 현장 실무자의 목소리를 담았습니다.",
                highlight: "업무 효율 100배 향상",
              },
            ].map((feature, i) => {
              const colors = {
                blue: {
                  bg: "bg-gray-50",
                  icon: "text-gray-600",
                  badge: "bg-gray-100 text-gray-700",
                },
                indigo: {
                  bg: "bg-gray-50",
                  icon: "text-gray-600",
                  badge: "bg-gray-100 text-gray-700",
                },
                purple: {
                  bg: "bg-gray-50",
                  icon: "text-gray-600",
                  badge: "bg-gray-100 text-gray-700",
                },
              };
              const c = colors[feature.color];
              return (
                <div
                  key={i}
                  className={`${c.bg} rounded-2xl p-8 border border-gray-100 hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group ${visibleSections["features"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
                  style={{ transitionDelay: `${i * 150}ms` }}
                >
                  <div
                    className={`w-14 h-14 rounded-xl ${c.bg} border border-gray-200/50 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}
                  >
                    <feature.icon className={`w-7 h-7 ${c.icon}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-gray-400 mb-4 font-medium">
                    {feature.subtitle}
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${c.badge}`}
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {feature.highlight}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Detail Features ─── */}
      <section className="py-24 bg-gray-50" data-animate id="detail-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 transition-all duration-700 ${visibleSections["detail-features"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            <p
              className="text-sm font-bold tracking-widest uppercase mb-3"
              style={{ color: SOFT_TX }}
            >
              Features
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900">
              실무자를 위한 강력한 기능
            </h2>
          </div>

          <div
            className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ${visibleSections["detail-features"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            {[
              {
                icon: Layers,
                title: "템플릿 관리",
                desc: "화주별 상이한 서식에 맞춤 필드를 설정하고 즉시 적용",
              },
              {
                icon: Scan,
                title: "교차 검증",
                desc: "AI 추출 결과를 다중 모델로 교차 비교하여 정확도 극대화",
              },
              {
                icon: Download,
                title: "다양한 내보내기",
                desc: "Excel, PDF, PNG 등 다양한 형식 지원 및 클립보드 복사",
              },
              {
                icon: BarChart3,
                title: "ERP 연동",
                desc: "RESTful API로 기존 ERP, uFMS, ELVIS와 원클릭 연동",
              },
              {
                icon: Table2,
                title: "복합 테이블",
                desc: "병합 셀, 중첩 표 등 복잡한 구조도 정확하게 해석",
              },
              {
                icon: Eye,
                title: "숫자 표기 전환",
                desc: "유럽식(.)과 미국식(,) 천 단위 구분을 토글로 전환",
              },
              {
                icon: ShieldCheck,
                title: "보안 암호화",
                desc: "OAuth2.0 + AES-256 암호화로 무역 데이터 안전 보호",
              },
              {
                icon: Zap,
                title: "대용량 배치",
                desc: "수백 장의 문서도 일괄 업로드 후 자동 병렬 처리",
              },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all duration-300 group"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition"
                  style={{ backgroundColor: "rgba(0,0,0,0.04)" }}
                >
                  <f.icon className="w-5 h-5" style={{ color: "#5f6368" }} />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{f.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Workflow ─── */}
      <section id="workflow" className="py-24 bg-white" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 transition-all duration-700 ${visibleSections["workflow"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            <p
              className="text-sm font-bold tracking-widest uppercase mb-3"
              style={{ color: SOFT_TX }}
            >
              How it works
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              3단계로 끝나는 문서 처리
            </h2>
            <p className="text-lg text-gray-500">
              서류 업로드부터 엑셀 내보내기까지, 클릭 몇 번이면 끝.
            </p>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-3 gap-0 md:gap-0 relative transition-all duration-700 ${visibleSections["workflow"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-[16.5%] right-[16.5%] h-0.5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />

            {[
              {
                step: "01",
                title: "서류 업로드",
                desc: "PDF, 이미지, 스캔본 — 어떤 형식이든 드래그 앤 드롭으로 업로드. 수백 장 일괄 처리 가능.",
                color: "blue",
              },
              {
                step: "02",
                title: "AI 자동 추출",
                desc: "VLM이 문서의 표 구조, 텍스트, 문맥을 이해하여 정형 데이터로 자동 변환. 교차 검증까지.",
                color: "indigo",
              },
              {
                step: "03",
                title: "검증 & 내보내기",
                desc: "그리드에서 결과를 확인하고 수정. Excel, ERP 연동, 클립보드 복사로 즉시 업무에 활용.",
                color: "purple",
              },
            ].map((item, i) => {
              return (
                <div
                  key={i}
                  className="relative px-6 md:px-8 py-8 text-center"
                  style={{ transitionDelay: `${i * 200}ms` }}
                >
                  <div
                    className={`w-14 h-14 mx-auto text-white rounded-2xl flex items-center justify-center text-xl font-extrabold mb-6 ring-8 ring-gray-100 shadow-lg relative z-10`}
                    style={{ backgroundColor: "#1a1a1a" }}
                  >
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-500 leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Target Customers ─── */}
      <section
        id="customers"
        className="py-24 bg-gradient-to-b from-gray-50 to-white"
        data-animate
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 transition-all duration-700 ${visibleSections["customers"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            <p
              className="text-sm font-bold tracking-widest uppercase mb-3"
              style={{ color: SOFT_TX }}
            >
              For your industry
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              당신의 업무를 위해 만들어졌습니다
            </h2>
            <p className="text-lg text-gray-500">
              수출입 물류의 모든 참여자를 위한 맞춤형 솔루션
            </p>
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-700 ${visibleSections["customers"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            {[
              {
                icon: Briefcase,
                title: "관세사 · 통관 법인",
                pain: "다량의 인보이스 수기 입력 시 오타, 숫자 오인식으로 인한 과태료 리스크",
                solution:
                  "99% 정확도 추출 + 줄자 도구 정밀 검증. Excel 내보내기로 관세 시스템에 즉시 전송.",
                color: "from-gray-700 to-gray-800",
              },
              {
                icon: Ship,
                title: "포워더 · 특송사",
                pain: "uFMS, ELVIS 등에 반복적 수기 입력. 직원 이탈률 증가.",
                solution:
                  "문서 → 데이터 → ERP 전 과정 자동화. API 연동으로 수기 입력 제로화.",
                color: "from-gray-800 to-gray-900",
              },
              {
                icon: Building2,
                title: "수출입 기업",
                pain: "PDF에 갇힌 데이터로 실시간 재고/물류비 파악 불가.",
                solution:
                  "비구조적 문서를 검색 가능한 디지털 자산으로 전환. ERP 실시간 연동.",
                color: "from-gray-600 to-gray-700",
              },
              {
                icon: Truck,
                title: "운송사",
                pain: "현장 영수증, 납부 서류의 처리 지연 및 누락.",
                solution:
                  "모바일 환경 지원. 영수증·납부서류 특화 OCR로 즉각적인 데이터 수집.",
                color: "from-gray-500 to-gray-600",
              },
            ].map((customer, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl transition-all duration-300 group"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex items-start gap-5">
                  <div
                    className={`w-12 h-12 rounded-xl bg-gradient-to-br ${customer.color} flex items-center justify-center flex-shrink-0 shadow-lg`}
                  >
                    <customer.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      {customer.title}
                    </h3>
                    <div className="mb-3">
                      <span className="text-xs font-bold text-red-500 uppercase tracking-wide">
                        Pain Point
                      </span>
                      <p className="text-sm text-gray-500 mt-1">
                        {customer.pain}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-bold text-green-600 uppercase tracking-wide">
                        DocuAI 솔루션
                      </span>
                      <p className="text-sm text-gray-700 mt-1 font-medium">
                        {customer.solution}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Pricing ─── */}
      <section id="pricing" className="py-24 bg-white" data-animate>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`text-center mb-16 transition-all duration-700 ${visibleSections["pricing"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            <p
              className="text-sm font-bold tracking-widest uppercase mb-3"
              style={{ color: SOFT_TX }}
            >
              Pricing
            </p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              라이센스 구독
            </h2>
            <p className="text-lg text-gray-500 mb-8">
              개인부터 팀까지, DocuAI에 맞는 요금제를 선택하세요.
            </p>

            {/* 개인 / 비즈니스 Toggle */}
            <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setActivePricing("personal")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${activePricing !== "business" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
              >
                개인
              </button>
              <button
                onClick={() => setActivePricing("business")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition ${activePricing === "business" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}
              >
                비즈니스
              </button>
            </div>
          </div>

          {activePricing !== "business" ? (
            /* ─── 개인 요금제 ─── */
            <div
              className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-700 ${visibleSections["pricing"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
            >
              {[
                {
                  name: "FREE",
                  price: "0원",
                  period: "/월",
                  desc: "기본 기능으로 시작하기",
                  pages: "월 100페이지 사용 가능",
                  features: [
                    "기본 OCR",
                    "기초 딥러닝",
                    "단일 사용자",
                    "커뮤니티 지원",
                    "샘플 템플릿",
                  ],
                  cta: "무료가입",
                  popular: false,
                  color: "bg-gray-50",
                  ctaStyle: "bg-gray-100 text-gray-700 hover:bg-gray-200",
                },
                {
                  name: "Starter",
                  price: "11,000원",
                  period: "/월",
                  desc: "부가세 포함",
                  pages: "월 700페이지 사용 가능",
                  features: [
                    "헤더 추출 + 템플릿 생성",
                    "템플릿 저장 3개",
                    "기본 편집 기능",
                    "클라우드 문서 보관",
                  ],
                  cta: "이 요금제로 시작",
                  popular: false,
                  color: "bg-white",
                  ctaStyle: "bg-[#1b1a20] text-white hover:bg-[#16151a]",
                },
                {
                  name: "Pro",
                  price: "33,000원",
                  period: "/월",
                  desc: "부가세 포함",
                  pages: "월 2,500페이지 사용 가능",
                  features: [
                    "Starter 전체 기능",
                    "템플릿 저장 20개",
                    "찾기/바꾸기 편집 기능",
                    "이미지(PNG,JPG) 파일 지원",
                    "템플릿 공유 가능",
                  ],
                  cta: "이 요금제로 시작",
                  popular: true,
                  color: "bg-white border-[#1b1a20]/30",
                  ctaStyle: "bg-[#1b1a20] text-white hover:bg-[#16151a]",
                },
                {
                  name: "Premium",
                  price: "55,000원",
                  period: "/월",
                  desc: "부가세 포함",
                  pages: "월 4,500페이지 사용 가능",
                  features: [
                    "Pro 전체 기능",
                    "템플릿 저장 무제한",
                    "엑셀 파일 지원",
                    "API 제공",
                  ],
                  cta: "이 요금제로 시작",
                  popular: false,
                  color: "bg-white",
                  ctaStyle: "bg-[#1b1a20] text-white hover:bg-[#16151a]",
                },
              ].map((plan, i) => (
                <div
                  key={i}
                  className={`rounded-2xl p-6 border ${plan.popular ? "border-[#1b1a20]/40 ring-2 ring-[#1b1a20]/10" : "border-gray-200"} ${plan.color} relative transition-all hover:shadow-lg`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#1b1a20] text-white text-xs font-bold rounded-full shadow">
                      추천
                    </div>
                  )}
                  <h3 className="text-lg font-bold mb-1 text-gray-900">
                    {plan.name}
                  </h3>
                  <p className="text-xs mb-2 text-gray-500">{plan.desc}</p>
                  <div className="mb-2">
                    <span className="text-3xl font-extrabold text-gray-900">
                      {plan.price}
                    </span>
                    <span className="text-sm ml-1 text-gray-500">
                      {plan.period}
                    </span>
                  </div>
                  {plan.pages && (
                    <p className="text-xs text-gray-400 mb-4">{plan.pages}</p>
                  )}
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((f, fi) => (
                      <li
                        key={fi}
                        className="flex items-center gap-2 text-sm text-gray-600"
                      >
                        <CheckCircle
                          className={`w-4 h-4 flex-shrink-0 ${plan.popular ? "text-green-500" : "text-green-500"}`}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={onLoginClick}
                    className={`w-full py-3 rounded-xl font-bold text-sm transition ${plan.ctaStyle || "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
                  >
                    {plan.cta}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            /* ─── 비즈니스 요금제 ─── */
            <div
              className={`transition-all duration-700 ${visibleSections["pricing"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                {/* 왼쪽: 인원수 선택 + 혜택 */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    인원 수 선택
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-8">
                    {[3, 4, 5, 7, 10, 15, 20].map((n) => (
                      <button
                        key={n}
                        onClick={() => setSelectedSeats(n)}
                        className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                          selectedSeats === n
                            ? "bg-gray-900 text-white border-gray-900"
                            : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        {n}명
                      </button>
                    ))}
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    비즈니스 라이센스 혜택
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "팀 협업 기능",
                      "개인 Premium 전체 기능 포함",
                      "전용 상담 채널",
                      "사용 통계 대시보드",
                      "온보딩 담당자",
                    ].map((b, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <CheckCircle className="w-4 h-4 flex-shrink-0 text-green-500" />
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* 오른쪽: 요금 카드 */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    비즈니스 기본 요금{" "}
                    <span className="text-xs font-normal text-gray-500">
                      (부가세 포함)
                    </span>
                  </h3>
                  <div className="mt-4 space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">인원 수</span>
                      <span className="font-semibold text-gray-900">
                        {selectedSeats}명
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">월 페이지 수</span>
                      <span className="font-semibold text-gray-900">
                        {businessPricing[selectedSeats].pages} 페이지
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">기준 금액</span>
                      <span className="font-semibold text-gray-900">
                        {businessPricing[selectedSeats].base.toLocaleString()}원
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">할인</span>
                      <span className="font-semibold text-red-500">
                        {businessPricing[selectedSeats].discount > 0
                          ? `-${businessPricing[selectedSeats].discount.toLocaleString()}원`
                          : "0원"}
                      </span>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 mt-4 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        월 결제 금액
                      </span>
                      <span className="text-3xl font-extrabold text-gray-900">
                        {businessPricing[selectedSeats].total.toLocaleString()}
                        원
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={onLoginClick}
                    className="w-full mt-6 py-3 rounded-xl font-bold text-sm transition bg-[#1b1a20] text-white hover:bg-[#16151a] shadow-lg shadow-[#1b1a20]/20"
                  >
                    바로 결제
                  </button>
                  <button
                    onClick={onLoginClick}
                    className="w-full mt-2 py-3 rounded-xl font-bold text-sm transition bg-white text-gray-600 border border-gray-300 hover:bg-gray-50"
                  >
                    담당자에게 문의
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ─── Social Proof / Stats ─── */}
      <section
        className="py-24 relative overflow-hidden"
        data-animate
        id="social-proof"
        style={{
          background: "#f7f8fc",
          borderTop: "1px solid #ecedf2",
          borderBottom: "1px solid #ecedf2",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`text-center transition-all duration-700 ${visibleSections["social-proof"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            <p
              className="text-sm font-bold tracking-widest uppercase mb-3"
              style={{ color: SOFT_TX }}
            >
              By the numbers
            </p>
            <h2
              className="text-3xl sm:text-4xl font-extrabold mb-4"
              style={{ color: "#16151a" }}
            >
              숫자가 증명합니다
            </h2>
            <p className="text-lg mb-16" style={{ color: "#6b7280" }}>
              DocuAI를 선택한 기업들이 경험한 변화
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: "99%", label: "데이터 추출 정확도" },
                { value: "80%", label: "수기 업무 시간 단축" },
                { value: "100x", label: "기존 대비 처리 속도" },
                { value: "2주", label: "평균 도입 기간" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="text-center bg-white rounded-2xl py-8 px-4 border"
                  style={{ borderColor: "#ecedf2" }}
                >
                  <div
                    className="text-4xl sm:text-5xl font-extrabold mb-2"
                    style={{ color: "#16151a" }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-sm" style={{ color: "#6b7280" }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CTA Section ─── */}
      <section className="py-24 bg-white" data-animate id="cta-section">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div
            className={`transition-all duration-700 ${visibleSections["cta-section"] ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              수기 입력의 번거로움에서
              <br />
              <span className="text-gray-900 font-extrabold">
                지금 벗어나세요
              </span>
            </h2>
            <p className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto">
              7일간 무료로 모든 기능을 체험하세요. 신용카드가 필요 없습니다.
              <br />
              2분이면 시작할 수 있습니다.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button
                onClick={onLoginClick}
                className="px-10 py-4 rounded-xl font-bold text-lg transition-all flex items-center justify-center gap-2 hover:opacity-90"
                style={{
                  background: DARK,
                  color: "#fff",
                  boxShadow: "0 10px 24px -8px rgba(20,21,26,0.35)",
                }}
              >
                무료로 시작하기
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={onLoginClick}
                className="px-10 py-4 rounded-xl bg-gray-100 text-gray-700 font-bold text-lg border border-gray-200 hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
              >
                전문가 상담 신청
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-gray-950 text-gray-400 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="/docuai_logo.png"
                  alt="DocuAI"
                  className="w-8 h-8 rounded-lg"
                />
                <span
                  className="text-xl font-extrabold tracking-tight"
                  style={{ color: "#e0e0e0" }}
                >
                  DocuAI
                </span>
              </div>
              <p className="text-sm leading-relaxed mb-6 max-w-sm">
                수출입 물류 비정형 문서 추출의 새로운 표준.
                <br />
                로그인네트웍이 만드는 물류 업무의 여유.
              </p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                  OAuth2.0
                </span>
                <span className="text-gray-600">·</span>
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                  AES-256 암호화
                </span>
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4 text-sm">서비스</h4>
              <ul className="space-y-3 text-sm">
                <li className="hover:text-white transition cursor-pointer">
                  핵심 기능
                </li>
                <li className="hover:text-white transition cursor-pointer">
                  요금 안내
                </li>
                <li className="hover:text-white transition cursor-pointer">
                  API 문서
                </li>
                <li className="hover:text-white transition cursor-pointer">
                  고객 사례
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4 text-sm">고객지원</h4>
              <ul className="space-y-3 text-sm">
                <li className="hover:text-white transition cursor-pointer">
                  공지사항
                </li>
                <li className="hover:text-white transition cursor-pointer">
                  자주 묻는 질문
                </li>
                <li className="hover:text-white transition cursor-pointer">
                  문의하기
                </li>
                <li className="hover:text-white transition cursor-pointer">
                  이용약관
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-xs mb-4">
              <span>
                &copy; {new Date().getFullYear()} LOGINNETWORK Co., Ltd. All
                rights reserved.
              </span>
              <span className="mt-2 md:mt-0">
                수출입 물류 자동화의 새로운 표준
              </span>
            </div>
            <div className="text-xs space-y-1 text-center md:text-left">
              <p>
                (주)로그인네트웍 | 대표자: 김정민 | 사업자등록번호: 121-86-03351
              </p>
              <p>
                연락처: 032-744-6000 | 주소: 인천광역시 중구 화랑목로 40-8
                (운서동) 102호, 103호, 104호
              </p>
              <p>통신판매신고번호: 2019-인천중구-0407</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
